import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import PullToRefresh from "@/components/PullToRefresh";
import {
  Hand, MapPin, Clock, Zap, RefreshCw, Search, Filter,
  Waves, CalendarClock, ExternalLink,
} from "lucide-react";

const PRIORITY_STYLE = {
  P1: { bg: "#FEE2E2", fg: "#991B1B", label: "P1 · Kritis" },
  P2: { bg: "#FFEDD5", fg: "#9A3412", label: "P2 · Tinggi" },
  P3: { bg: "#DBEAFE", fg: "#1E40AF", label: "P3 · Normal" },
  P4: { bg: "#F3F4F6", fg: "#4B5563", label: "P4 · Rendah" },
};

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
  } catch { return iso; }
}

export default function TicketPool() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [q, setQ] = useState("");
  const [wilayahFilter, setWilayahFilter] = useState("");
  const [prioritasFilter, setPrioritasFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (wilayahFilter) params.wilayah = wilayahFilter;
      if (prioritasFilter) params.prioritas = prioritasFilter;
      const { data } = await api.get("/tickets/pool", { params });
      setTickets(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memuat daftar klaim");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const wilayahOptions = Array.from(new Set(tickets.map(t => t.wilayah).filter(Boolean))).sort();

  const claim = async (t) => {
    setClaimingId(t.id);
    try {
      await api.post(`/tickets/${t.id}/claim`);
      toast.success(`Tiket ${t.no_tiket} berhasil diklaim`);
      // remove from list optimistically
      setTickets((rows) => rows.filter((r) => r.id !== t.id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal klaim");
    } finally { setClaimingId(null); }
  };

  return (
    <PullToRefresh onRefresh={load}>
    <div className="space-y-6" data-testid="pool-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] flex items-center gap-1.5">
            <Waves className="h-3 w-3 text-[#0055FF]" />
            Operasional
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tighter mt-1">
            Klaim Tiket Terbuka
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            {tickets.length === 0
              ? "Belum ada tiket menunggu diklaim — kerja bagus!"
              : `${tickets.length} tiket siap diklaim. Klik “Klaim” untuk menjadi PIC.`}
          </p>
        </div>
        <button
          data-testid="pool-refresh"
          onClick={load}
          className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8F9FA] text-sm font-semibold text-[#4B5563] flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Muat Ulang
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-[#E5E7EB] rounded-md p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
          <input
            data-testid="pool-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Cari no tiket, lokasi, keterangan..."
            className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E5E7EB] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
          />
        </div>
        <select
          data-testid="pool-filter-wilayah"
          value={wilayahFilter}
          onChange={(e) => setWilayahFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm"
        >
          <option value="">Semua Wilayah</option>
          {wilayahOptions.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <select
          data-testid="pool-filter-prioritas"
          value={prioritasFilter}
          onChange={(e) => setPrioritasFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm"
        >
          <option value="">Semua Prioritas</option>
          <option value="P1">P1 · Kritis</option>
          <option value="P2">P2 · Tinggi</option>
          <option value="P3">P3 · Normal</option>
          <option value="P4">P4 · Rendah</option>
        </select>
        <button
          data-testid="pool-apply-filter"
          onClick={load}
          className="h-10 px-4 rounded-md bg-[#0A0A0A] hover:bg-[#1F2937] text-white text-sm font-semibold flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          Terapkan
        </button>
      </div>

      {/* Feed */}
      {loading && tickets.length === 0 ? (
        <div className="text-sm text-[#9CA3AF] py-10 text-center">Memuat daftar klaim...</div>
      ) : tickets.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E7EB] rounded-md p-10 text-center">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Tidak Ada Antrean</div>
          <div className="mt-1 font-display text-xl font-bold">Tidak ada tiket menunggu</div>
          <div className="mt-1 text-sm text-[#4B5563]">Semua tiket sudah diklaim atau selesai. Cek lagi nanti.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="pool-grid">
          {tickets.map((t, idx) => {
            const p = PRIORITY_STYLE[t.prioritas] || PRIORITY_STYLE.P4;
            const isReschedule = t.status === "RESCHEDULE";
            return (
              <motion.article
                key={t.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.03, 0.3) }}
                data-testid={`pool-card-${t.id}`}
                className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div
                      className="inline-flex items-center gap-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest px-2 py-0.5"
                      style={{ background: p.bg, color: p.fg }}
                    >
                      <Zap className="h-3 w-3" strokeWidth={3} />
                      {p.label}
                    </div>
                    <div className="mt-1.5 font-mono-tab text-xs text-[#9CA3AF] truncate">
                      {t.no_tiket}
                    </div>
                  </div>
                  {isReschedule ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-md text-[10px] font-bold uppercase tracking-widest px-2 py-0.5"
                      style={{ background: "#FEF3C7", color: "#92400E" }}
                    >
                      <CalendarClock className="h-3 w-3" />
                      Reschedule
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 rounded-md text-[10px] font-bold uppercase tracking-widest px-2 py-0.5"
                      style={{ background: "#DBEAFE", color: "#1E40AF" }}
                    >
                      Baru
                    </span>
                  )}
                </div>

                <div className="min-w-0">
                  <div className="font-display text-lg font-bold leading-tight line-clamp-2">
                    {t.objek_lokasi || "-"}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#4B5563]">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-[#EA580C]" />
                      {t.wilayah || "-"}
                    </span>
                    {t.jenis_pekerjaan && (
                      <span className="inline-flex items-center gap-1">
                        <span className="h-1 w-1 rounded-full bg-[#9CA3AF]"></span>
                        {t.jenis_pekerjaan}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3 text-[#9CA3AF]" />
                      {fmtDate(t.tgl_pelaksanaan || t.tgl_masuk || t.created_at)}
                    </span>
                  </div>
                </div>

                {isReschedule && t.last_reschedule && (
                  <div
                    className="rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs leading-snug"
                    data-testid={`pool-card-reschedule-${t.id}`}
                  >
                    <div className="font-bold text-[#92400E] text-[10px] uppercase tracking-widest">
                      Ditunda oleh {t.last_reschedule.prev_pic || "tim sebelumnya"}
                    </div>
                    <div className="mt-0.5 text-[#78350F]">
                      <b>Alasan:</b> {t.last_reschedule.reason}
                    </div>
                    {t.last_reschedule.notes && (
                      <div className="text-[#78350F] mt-0.5 line-clamp-2">
                        {t.last_reschedule.notes}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-auto flex items-center gap-2 pt-2">
                  {t.lat && t.lng && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${t.lat},${t.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`pool-card-map-${t.id}`}
                      className="h-10 px-3 rounded-md border border-[#E5E7EB] hover:bg-[#F8F9FA] text-xs font-semibold text-[#4B5563] flex items-center gap-1.5"
                      title="Buka di Google Maps"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Rute
                    </a>
                  )}
                  <button
                    data-testid={`pool-card-claim-${t.id}`}
                    disabled={claimingId === t.id}
                    onClick={() => claim(t)}
                    className="flex-1 h-10 rounded-md bg-[#0055FF] hover:bg-[#0044CC] disabled:bg-[#9CA3AF] text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                  >
                    {claimingId === t.id ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Mengklaim...
                      </>
                    ) : (
                      <>
                        <Hand className="h-4 w-4" />
                        Klaim Sekarang
                      </>
                    )}
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>
      )}
    </div>
    </PullToRefresh>
  );
}
